/**
 * Accessibility Initialization Utility
 * 
 * Initializes accessibility features on page load based on saved user preferences.
 * This script handles high contrast mode and font size settings.
 */
import ReactDOM from 'react-dom/client';
import ReadAloud from '../components/accessibility/ReadAloud';
import React from 'react';

const initAccessibilitySettings = (): void => {
  if (typeof window === 'undefined') return;
  
  // Initialize high contrast mode
  const savedHighContrast = localStorage.getItem('high-contrast');
  if (savedHighContrast === 'true') {
    document.documentElement.classList.add('high-contrast');
  }
  
  // Initialize font size
  const savedFontSizeLevel = localStorage.getItem('font-size-level');
  if (savedFontSizeLevel) {
    const fontSizeLevelMap: Record<string, string> = {
      'default': '1rem',
      'medium': '1.125rem',
      'large': '1.25rem',
      'x-large': '1.375rem'
    };
    
    if (fontSizeLevelMap[savedFontSizeLevel]) {
      document.documentElement.style.setProperty('--base-font-size', fontSizeLevelMap[savedFontSizeLevel]);
    }
  }
  
  // Initialize voice assistant if saved preference exists
  const savedVoiceAssistant = localStorage.getItem('voice-assistant');
  if (savedVoiceAssistant === 'true') {
    // Store this in a global variable that the app can check
    window.__voiceAssistantEnabled = true;
    // The actual component will be shown by the AccessibilityMenu component
  } else {
    window.__voiceAssistantEnabled = false;
  }
  
  // Handle high contrast mode preference changes via system settings
  // (prefers-contrast media query)
  const prefersContrastMediaQuery = window.matchMedia('(prefers-contrast: more)');
  
  function handlePrefersContrastChange(e: MediaQueryListEvent | MediaQueryList) {
    // Only apply if user hasn't explicitly set a preference
    if (localStorage.getItem('high-contrast') === null) {
      if (e.matches) {
        document.documentElement.classList.add('high-contrast');
      } else {
        document.documentElement.classList.remove('high-contrast');
      }
    }
  }
  
  // Initial check for system preference
  handlePrefersContrastChange(prefersContrastMediaQuery);
  
  // Listen for changes to system preference
  try {
    // Modern browsers
    prefersContrastMediaQuery.addEventListener('change', handlePrefersContrastChange);
  } catch (e) {
    // Fallback for older browsers that don't support addEventListener on MediaQueryList
    prefersContrastMediaQuery.addListener(handlePrefersContrastChange);
  }
  
  // Set up the reduced motion preference
  const prefersReducedMotionMediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  
  function handleReducedMotionChange(e: MediaQueryListEvent | MediaQueryList) {
    if (e.matches) {
      document.documentElement.classList.add('reduced-motion');
    } else {
      document.documentElement.classList.remove('reduced-motion');
    }
  }
  
  // Initial check for reduced motion preference
  handleReducedMotionChange(prefersReducedMotionMediaQuery);
  
  // Listen for changes to reduced motion preference
  try {
    prefersReducedMotionMediaQuery.addEventListener('change', handleReducedMotionChange);
  } catch (e) {
    prefersReducedMotionMediaQuery.addListener(handleReducedMotionChange);
  }

  // Setup text selection listener for read aloud feature
  setupTextSelectionListener();
};

// Add TypeScript declaration for global voice assistant preference
declare global {
  interface Window {
    __voiceAssistantEnabled?: boolean;
  }
}

// Add text selection listener for read aloud functionality
function setupTextSelectionListener() {
  let readAloudRoot: any = null;
  let readAloudContainer: HTMLDivElement | null = null;

  // Function to create floating read-aloud button
  const createReadAloudButton = (text: string, x: number, y: number) => {
    // Create container if it doesn't exist
    if (!readAloudContainer) {
      readAloudContainer = document.createElement('div');
      readAloudContainer.id = 'read-aloud-container';
      readAloudContainer.style.position = 'absolute';
      readAloudContainer.style.zIndex = '1000';
      document.body.appendChild(readAloudContainer);
      
      // Create React root
      readAloudRoot = ReactDOM.createRoot(readAloudContainer);
    }
    
    // Update position
    readAloudContainer.style.left = `${x}px`;
    readAloudContainer.style.top = `${y}px`;
    
    // Render ReadAloud component
    readAloudRoot.render(React.createElement(ReadAloud, { text }));
  };
  
  // Function to remove the button
  const removeReadAloudButton = () => {
    if (readAloudContainer) {
      readAloudRoot.unmount();
      document.body.removeChild(readAloudContainer);
      readAloudContainer = null;
      readAloudRoot = null;
    }
  };
  
  // Listen for text selection
  document.addEventListener('mouseup', () => {
    const selection = window.getSelection();
    const selectedText = selection?.toString().trim();
    
    // Remove existing button
    removeReadAloudButton();
    
    // If there's selected text, show the button
    if (selectedText && selectedText.length > 10) {
      // Get selection coordinates
      const range = selection?.getRangeAt(0);
      const rect = range?.getBoundingClientRect();
      
      if (rect) {
        const x = window.scrollX + rect.left;
        const y = window.scrollY + rect.bottom + 10; // Position it below the text
        createReadAloudButton(selectedText, x, y);
      }
    }
  });
  
  // Listen for clicks outside selected text to remove the button
  document.addEventListener('mousedown', (e) => {
    if (readAloudContainer && !readAloudContainer.contains(e.target as Node)) {
      removeReadAloudButton();
    }
  });
  
  // Also listen for escape key to remove the button
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && readAloudContainer) {
      removeReadAloudButton();
    }
  });
}

export default initAccessibilitySettings; 